import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Hexagon, ShieldCheck, TrendingUp, MapPin, ArrowRight, Zap, Clock, Lock } from "lucide-react";
import { HexLogo, BrandLockup } from "@/components/dashboard/Hex";
import { BrutalButton } from "@/components/dashboard/BentoCard";
import { MarketingFooter } from "@/components/marketing/MarketingShell";
import { InstallPrompt } from "@/components/marketing/InstallPrompt";

export const LANDING = {
  root: "landing-page",
  primaryCta: "landing-primary-cta",
  secondaryCta: "landing-secondary-cta",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const go = () => navigate("/login");

  return (
    <div data-testid={LANDING.root} className="min-h-screen bg-honeycomb text-black">
      {/* Top strip */}
      <div className="bg-red-600 text-white text-center py-2 font-mono-data text-[11px] uppercase tracking-widest">
        AI scores every listing · alerts before the auction · 3-day free trial
      </div>

      {/* Nav */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-3">
          <HexLogo size={48} />
          <div>
            <div className="font-brand text-xl leading-none text-red-600">
              LIEN SCOUT
            </div>
            <div className="font-mono-data text-[9px] uppercase tracking-[0.18em] text-zinc-600 mt-1.5">
              AI Powered Property Intelligence
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-display font-semibold text-zinc-700">
          <Link to="/features" className="hover:text-red-600" data-testid="marketing-nav-features">Features</Link>
          <Link to="/pricing" className="hover:text-red-600" data-testid="marketing-nav-pricing">Pricing</Link>
          <Link to="/coverage" className="hover:text-red-600" data-testid="marketing-nav-coverage">Coverage</Link>
          <Link to="/about" className="hover:text-red-600" data-testid="marketing-nav-about">About</Link>
          <Link to="/contact" className="hover:text-red-600" data-testid="marketing-nav-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden sm:inline font-display text-sm font-semibold underline decoration-yellow-500 decoration-2 underline-offset-4" data-testid="marketing-nav-signin">
            Sign in
          </Link>
          <BrutalButton data-testid={LANDING.primaryCta} onClick={go}>
            Start free trial <ArrowRight size={14} />
          </BrutalButton>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black bg-yellow-300 font-mono-data text-[10px] uppercase tracking-widest">
            <Zap size={12} /> AI-scored · Nationwide · Auction alerts
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
            The list is free.<br />
            The <span className="text-red-600">shortlist</span> is what<br />
            you're paying for.
          </h1>
          <p className="mt-5 text-lg text-zinc-700 max-w-lg">
            Anyone can dig through 3,000 county PDFs. Lien Scout uses AI to
            score every listing on equity, condition, and win-probability so
            you skip the 90% of junk and get straight to the deals that
            actually move.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <BrutalButton
              data-testid={LANDING.primaryCta + "-hero"}
              onClick={go}
              className="text-base py-3 px-6"
            >
              Start your 3-day free trial <ArrowRight size={16} />
            </BrutalButton>
            <Link
              to="/coverage"
              data-testid={LANDING.secondaryCta}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-black bg-white font-display font-semibold shadow-brutal-sm hover:bg-zinc-50"
            >
              See coverage map
            </Link>
          </div>
          <div className="mt-6 flex items-center gap-4 font-mono-data text-[11px] uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1"><Clock size={12} /> Auction alerts before sale day</span>
            <span className="flex items-center gap-1"><ShieldCheck size={12} /> Full deal history</span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 bg-honeycomb-strong opacity-30 -z-10" />
          <img
            src="/marketing/hero-map.png"
            alt="Nationwide hex-tile map of live tax-sale coverage"
            className="border-2 border-black shadow-brutal w-full h-auto"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      </section>

      {/* Value props */}
      <section className="bg-white border-y-2 border-black">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {VALUE_PROPS.map((v) => (
            <div key={v.title} className="border-2 border-black p-6 bg-white">
              <div className="w-10 h-10 border-2 border-black flex items-center justify-center bg-yellow-300">
                <v.icon size={20} strokeWidth={2.5} />
              </div>
              <div className="mt-4 font-display font-bold text-xl">{v.title}</div>
              <p className="mt-2 text-sm text-zinc-600">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Preview strip */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="data-label">What you actually get</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tight">
            Every listing gets a <span className="text-red-600">Deal Score</span>.
          </h2>
          <p className="mt-4 text-zinc-700">
            Our AI cross-references estimated equity, redemption probability,
            comparable sales, and county-specific win-rates — then scores every
            listing 0-100 so the top ten deals bubble up before you even open
            a filter.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <ValueLine icon={TrendingUp} text="AI Deal Score on every property (higher = better)" />
            <ValueLine icon={MapPin} text="Cross-metro filtering — one investor, 50 states" />
            <ValueLine icon={Lock} text="Owner, parcel, and amount data unlocked when you subscribe" />
          </ul>
        </div>
        <div>
          <img
            src="/marketing/hero-phone.png"
            alt="Phone dashboard with masked property preview and deal score"
            className="border-2 border-black shadow-brutal w-full max-w-sm mx-auto"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      </section>

      {/* Pricing peek */}
      <section className="bg-red-600 text-white border-y-2 border-black">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <div className="font-mono-data text-[11px] uppercase tracking-[0.24em]">
            $9.99 / month · $25.99 / 3 months · $75 / year
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight">
            $9.99 buys back your weekend.
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-yellow-100">
            The average tax-sale investor spends 12-20 hours a week trawling
            county sites. That's your entire Saturday. Lien Scout does it in
            30 seconds. All plans unlock every listing, every score, and every
            alert — 3 days free, cancel any time.
          </p>
          <BrutalButton
            data-testid={LANDING.primaryCta + "-cta"}
            onClick={go}
            className="mt-8 py-3 px-8 text-base bg-yellow-400 hover:bg-yellow-300 text-black"
          >
            Start free trial <ArrowRight size={16} />
          </BrutalButton>
          <p className="mt-4 text-[11px] font-mono-data uppercase tracking-widest text-yellow-100/80 max-w-md mx-auto">
            Lien Scout scores public tax-sale listings. Deal scores are estimates — always verify before bidding.
          </p>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
      <InstallPrompt />
    </div>
  );
}

const VALUE_PROPS = [
  {
    icon: TrendingUp,
    title: "AI Deal Score, not just a data dump.",
    body: "We rank every listing on estimated equity, condition, and win-probability. Skip the 90% of junk that clogs county lists and see only the deals worth chasing.",
  },
  {
    icon: Zap,
    title: "Auction alerts before sale day.",
    body: "Sales fire on strict county schedules — miss the date, lose the deal. Get notified the moment your saved filter matches a new listing.",
  },
  {
    icon: MapPin,
    title: "One dashboard beats 3,000 counties.",
    body: "Every county has its own PDF, spreadsheet, or broken login page. We normalize all of them into one filterable view, updated every 30 minutes.",
  },
];

function ValueLine({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-7 h-7 border-2 border-black bg-yellow-300 flex items-center justify-center shrink-0">
        <Icon size={12} strokeWidth={2.5} />
      </div>
      <span className="pt-1">{text}</span>
    </li>
  );
}
